import { useState, useRef, useEffect, useContext } from 'react';
import axios from 'axios';
import { useAuthHeader, buildUrl } from 'utils';

const server = process.env.REACT_APP_BACKEND_ENDPOINT;

export function useFileUpload(route, triggerOnUpload = false) {

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [savedFiles, setSavedFiles] = useState([]);
    const [response, setResponse] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    let fakeTimer = useRef(null);
    const fakeProgress = useRef(0);

    const { getHttpHeaders } = useAuthHeader()

    useEffect(() => {
        if (triggerOnUpload && selectedFiles.length > 0) {
            (async () => {
                await sendForm();
            })();
        }
    }, [triggerOnUpload, selectedFiles]);

    const addFiles = (files) => {
        setSelectedFiles((prev) => [
            ...prev,
            ...files.map((file) => ({ name: file.name, type: file.type, blob: file })),
        ]);
    };

    const getProgressInterval = (fileSizeBytes) => {
        const sizeMB = fileSizeBytes / (1024 * 1024); // Convert to MB
    
        if (sizeMB < 5) return 100;     // fast updates every 100ms
        if (sizeMB < 20) return 200;    // normal updates every 200ms
        if (sizeMB < 50) return 300;    // slower
        return 400;                     // slowest
    };
    
    const startFakeProgress = (fileSize) => {
        fakeProgress.current = 0;
        const interval = getProgressInterval(fileSize);
    
        fakeTimer.current = setInterval(() => {
            fakeProgress.current = Math.round(Math.min(fakeProgress.current + Math.random() * 5, 95));
            setUploadProgress(fakeProgress.current);
        }, interval);
    };
    
    const stopFakeProgress = () => {
        clearInterval(fakeTimer.current);
        setUploadProgress(100); // final jump
        setTimeout(() => setUploadProgress(0), 1000);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        const files = Array.from(e.dataTransfer.files);
        addFiles(files);
    };
    
    const changeFile = (event) => {
        const files = Array.from(event.target.files);
        addFiles(files);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const triggerFileUpload = () => {
        if(!fileInputRef.current){
            console.error("fileInputRef not set")
            return
        }
        fileInputRef.current.click();
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const deleteFile = async (filename) => {
        try {
            const headers = await getHttpHeaders();
            console.log(`${server}${route}/${filename}`)
            await axios.delete(`${server}${route}/${filename}`, { headers });
        } catch (error) {
            console.error('Error deleting image:', error);
        }
    };

    const sendFile = async (formData) => {
        setSavedFiles(null)
        setSuccess(null)
        setResponse(null)
        setError(null)

        try {
            let headers = await getHttpHeaders()
            headers['Content-Type'] = 'multipart/form-data'

            const queryString = buildUrl(server, `${route}`);
            if (selectedFiles.length > 0) {
                startFakeProgress(selectedFiles[0].blob.size);
            }
            const response = await axios.post(queryString, formData, {
                headers
            }); 
            stopFakeProgress();
            
            console.log(response);
            setSuccess({status: true, message: "File Uploaded Successfully"})
            setSavedFiles(response?.data?.files)
            setResponse(response?.data)
            setSelectedFiles([])
            return response?.data

        } catch (error) {
            console.error(error);
            if (error?.response?.status === 413) {
                setError({ status: true, message: "Die Datei ist zu groß. Maximale Größe: 100 MB." });
            } else {
                setError(error);
            }
            return false;
        } finally{
            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }
            setUploadProgress(0); 
        }
    }
    
    const sendForm = async () => {
        setIsUploading(true)
        
        const formData = new FormData();

        if (selectedFiles.length > 0) {
            selectedFiles.forEach(file => {
                if (file.blob) {
                    console.log(file)
                    formData.append('files', file.blob, file.name);
                }
            });
        }

        try {
            const result = await sendFile(formData);
            setSelectedFiles([]);
            setIsUploading(false)
            return result
        } catch (error) {
            console.log(error)
            setError(error)
            console.error('Failed to send form:', error);
        }
    }    

    return { selectedFiles, fileInputRef, changeFile, deleteFile, triggerFileUpload, removeFile, isUploading, error, 
        setError, success, response, setSuccess, savedFiles,  setSavedFiles, dragActive, handleDragEnter, 
        handleDragOver, handleDragLeave, handleDrop, setSelectedFiles, sendFile, uploadProgress };
};